/**
 * Owner dashboard charts (M12). Colour carries meaning together with shape
 * and labels, never alone: revenue is a solid area, expenses a dashed line,
 * every chart has a text summary and exact values on hover.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Banknote, HandCoins, TrendingUp, Truck } from 'lucide-react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { useTheme } from '../lib/theme';
import { Card, Skeleton } from '../components/ui';

export interface Trends {
  months: { month: string; label: string; revenue: number; expenses: number; netIncome: number; cash: number }[];
  expenseMix: { name: string; amount: number }[];
  topCustomers: { name: string; amount: number }[];
  arAging: { bucket: string; amount: number }[];
  apAging: { bucket: string; amount: number }[];
}

/** Vivid, distinguishable in light and dark, colour-blind-aware order. */
export const PALETTE = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#0EA5E9', '#8B5CF6', '#14B8A6', '#F43F5E'];
const AGING = ['#10B981', '#84CC16', '#F59E0B', '#F97316', '#F43F5E'];

const compact = (n: number) => `₱${Intl.NumberFormat('en-PH', { notation: 'compact', maximumFractionDigits: 1 }).format(n)}`;
const full = (n: number) => `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function useChartTheme() {
  const { resolved } = useTheme();
  const dark = resolved === 'dark';
  return {
    grid: dark ? '#1E293B' : '#E2E8F0',
    axis: dark ? '#94A3B8' : '#64748B',
    tooltip: { backgroundColor: dark ? '#0F172A' : '#FFFFFF', border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, borderRadius: 12, color: dark ? '#F1F5F9' : '#0F172A', fontSize: 12 },
  };
}

export function useTrends(enabled: boolean) {
  return useQuery({ queryKey: ['dash-trends'], queryFn: () => api<Trends>('/dashboard/trends'), enabled });
}

/** Gradient KPI tile. */
export function Kpi({ label, value, sub, to, gradient, Icon, trend }: { label: string; value: string; sub?: ReactNode; to: string; gradient: string; Icon: typeof Banknote; trend?: 'up' | 'down' }): JSX.Element {
  return (
    <Link to={to} className="group block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand/50">
      <div className={`relative h-full overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-5 text-white shadow-lg shadow-slate-900/10 transition group-hover:-translate-y-0.5 group-hover:shadow-xl`}>
        <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10" aria-hidden="true" />
        <div className="flex items-start justify-between">
          <p className="text-sm font-medium text-white/85">{label}</p>
          <span className="rounded-xl bg-white/20 p-2"><Icon aria-hidden="true" className="h-5 w-5" /></span>
        </div>
        <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight">{value}</p>
        {sub && (
          <p className="mt-1 flex items-center gap-1 text-xs text-white/85">
            {trend === 'up' && <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />}
            {trend === 'down' && <ArrowDownRight aria-hidden="true" className="h-3.5 w-3.5" />}
            {sub}
          </p>
        )}
      </div>
    </Link>
  );
}

export const KPI_STYLE = {
  cash: { gradient: 'from-sky-500 to-indigo-600', Icon: Banknote },
  ar: { gradient: 'from-emerald-500 to-teal-600', Icon: HandCoins },
  ap: { gradient: 'from-amber-500 to-orange-600', Icon: Truck },
  profit: { gradient: 'from-fuchsia-500 to-purple-600', Icon: TrendingUp },
};

export function TrendCharts({ data }: { data: Trends | undefined; loading?: boolean }): JSX.Element {
  const t = useChartTheme();
  if (!data) return <Skeleton className="h-80 w-full" />;
  const m = data.months;
  const revTotal = m.reduce((s, x) => s + x.revenue, 0);
  const expTotal = m.reduce((s, x) => s + x.expenses, 0);
  const best = [...m].sort((a, b) => b.netIncome - a.netIncome)[0];
  const mixTotal = data.expenseMix.reduce((s, x) => s + x.amount, 0);
  const arTotal = data.arAging.reduce((s, x) => s + x.amount, 0);
  const apTotal = data.apAging.reduce((s, x) => s + x.amount, 0);
  const aging = data.arAging.map((b, i) => ({ bucket: b.bucket, receivable: b.amount, payable: data.apAging[i]?.amount ?? 0 }));

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-3">
        <Card title="Revenue, expenses and profit" description={`Last 12 months: revenue ${compact(revTotal)}, expenses ${compact(expTotal)}. Best month ${best?.label ?? '—'} (${compact(best?.netIncome ?? 0)}).`} className="xl:col-span-2">
          <div className="h-72" role="img" aria-label={`Revenue ${full(revTotal)} and expenses ${full(expTotal)} over the last 12 months`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={m} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.45} /><stop offset="100%" stopColor="#10B981" stopOpacity={0.02} /></linearGradient>
                </defs>
                <CartesianGrid stroke={t.grid} vertical={false} />
                <XAxis dataKey="label" stroke={t.axis} tickLine={false} axisLine={false} fontSize={12} />
                <YAxis stroke={t.axis} tickLine={false} axisLine={false} fontSize={12} tickFormatter={compact} width={64} />
                <Tooltip contentStyle={t.tooltip} formatter={(v: number, n: string) => [full(v), n]} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2.5} fill="url(#rev)" />
                <Bar dataKey="netIncome" name="Net income" fill="#6366F1" barSize={14} radius={[6, 6, 0, 0]}>
                  {m.map((x) => <Cell key={x.month} fill={x.netIncome >= 0 ? '#6366F1' : '#F43F5E'} />)}
                </Bar>
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#F59E0B" strokeWidth={2.5} strokeDasharray="6 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Where the money went" description={`Expenses this year: ${compact(mixTotal)}`}>
          {data.expenseMix.length === 0 ? <p className="text-sm text-slate-500">No expenses yet this year.</p> : (
            <>
              <div className="h-48" role="img" aria-label="Expense mix this year">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.expenseMix} dataKey="amount" nameKey="name" innerRadius="58%" outerRadius="92%" paddingAngle={2} stroke="none">
                      {data.expenseMix.map((x, i) => <Cell key={x.name} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={t.tooltip} formatter={(v: number, n: string) => [`${full(v)} (${Math.round((v / mixTotal) * 100)}%)`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {data.expenseMix.map((x, i) => (
                  <li key={x.name} className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} /><span className="truncate">{x.name}</span></span>
                    <span className="tabular-nums text-slate-500">{Math.round((x.amount / mixTotal) * 100)}%</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card title="Cash on hand and in banks" description={`Month-end balance, now ${compact(m[m.length - 1]?.cash ?? 0)}`}>
          <div className="h-56" role="img" aria-label="Month-end cash balance">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={m} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cash" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.5} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0.05} /></linearGradient>
                </defs>
                <CartesianGrid stroke={t.grid} vertical={false} />
                <XAxis dataKey="label" stroke={t.axis} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke={t.axis} tickLine={false} axisLine={false} fontSize={11} tickFormatter={compact} width={56} />
                <Tooltip contentStyle={t.tooltip} formatter={(v: number) => [full(v), 'Cash']} />
                <Area type="monotone" dataKey="cash" stroke="#0EA5E9" strokeWidth={2.5} fill="url(#cash)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="How old are the balances?" description={`Customers owe ${compact(arTotal)}; you owe ${compact(apTotal)}`}>
          <div className="h-56" role="img" aria-label="Receivables and payables by age">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={t.grid} vertical={false} />
                <XAxis dataKey="bucket" stroke={t.axis} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke={t.axis} tickLine={false} axisLine={false} fontSize={11} tickFormatter={compact} width={56} />
                <Tooltip contentStyle={t.tooltip} formatter={(v: number, n: string) => [full(v), n]} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="receivable" name="Receivable" fill="#10B981" radius={[6, 6, 0, 0]}>{aging.map((x, i) => <Cell key={x.bucket} fill={AGING[i]} />)}</Bar>
                <Bar dataKey="payable" name="Payable" fill="#8B5CF6" fillOpacity={0.55} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Top customers this year" description="By invoiced amount">
          {data.topCustomers.length === 0 ? <p className="text-sm text-slate-500">No invoices yet this year.</p> : (
            <ul className="space-y-3">
              {data.topCustomers.map((c, i) => {
                const max = data.topCustomers[0].amount || 1;
                return (
                  <li key={c.name}>
                    <div className="mb-1 flex justify-between gap-2 text-sm"><span className="truncate font-medium">{c.name}</span><span className="tabular-nums text-slate-500">{compact(c.amount)}</span></div>
                    <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800">
                      <div className="h-2.5 rounded-full" style={{ width: `${Math.max(4, (c.amount / max) * 100)}%`, background: `linear-gradient(90deg, ${PALETTE[i % PALETTE.length]}, ${PALETTE[(i + 1) % PALETTE.length]})` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
